import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { callTool, ToolCallError } from '../lib/tools'
import useOrganizations from './useOrganizations'
import { getUserId } from '../lib/workos'
import { ownPersonalOrg } from '../utilities/defaultOrg'

export type OrgMember = {
  userId: string
  email: string
  name: string
  role: string
  joinedAt: string
}

// The snake_case shape org_members_list returns (camelcased on read).
type RawMember = {
  user_id: string
  email: string
  name: string
  role: string
  joined_at: string
}

export interface OrgMembersState {
  members: OrgMember[]
  set: (state: Partial<OrgMembersState>) => void
  get: (orgId?: string) => Promise<void>
  setRole: (orgId: string, userId: string, role: string) => Promise<void>
  remove: (orgId: string, userId: string) => Promise<void>
  transferOwnership: (orgId: string, userId: string) => Promise<void>
  leave: (orgId: string) => Promise<void>
  subscribe: (orgId?: string) => () => void
  reset: () => void
}

const state = {
  members: [],
}

// The roster is served by the router (org_members_list), not Supabase: profiles
// RLS is self-only, so a member can't read co-members' name/email via PostgREST.
// Role-gated writes route through callTool; the router is authoritative.
//
// ⚠ Only an org Owner or Admin may list (PERMISSIONS.md § 5 Org, router#1765, app#381). Callers
// check `mayListMembers` before `get()`; a Member's roster stays empty and the members page shows
// the Member their own membership instead.
const useOrgMembers = create<OrgMembersState>()((set, get) => ({
  ...state,
  set,
  get: async orgId => {
    if (!orgId) return
    try {
      const res = await callTool<{ members: RawMember[] }>('org_members_list', { org_id: orgId })
      const members = (res.members ?? []).map(m => camelcaseKeys(m)) as OrgMember[]
      // ⚠ Drop an answer for an org that is not active now. Without this, a roster that arrives
      // after a switch to an org where the caller is a Member (which clears the roster) comes back.
      if (useOrganizations.getState().activeOrgId !== orgId) return
      set({ members })
    } catch (error) {
      // The router refuses a caller who may not list (a Member since router#1765). Do not keep
      // a roster read earlier, for example before a demotion: the caller may not see it now.
      if (
        error instanceof ToolCallError &&
        error.code === 'forbidden' &&
        useOrganizations.getState().activeOrgId === orgId
      )
        set({ members: [] })
      console.warn(error)
    }
  },
  setRole: async (orgId, userId, role) => {
    await callTool('org_member_set_role', { org_id: orgId, user_id: userId, role })
    await get().get(orgId)
  },
  remove: async (orgId, userId) => {
    await callTool('org_member_remove', { org_id: orgId, user_id: userId })
    await get().get(orgId)
  },
  transferOwnership: async (orgId, userId) => {
    await callTool('org_transfer_ownership', { org_id: orgId, user_id: userId })
    // The caller is now an Admin — refresh org context (role) and the roster.
    await useOrganizations.getState().get()
    await get().get(orgId)
  },
  leave: async orgId => {
    await callTool('org_leave', { org_id: orgId })
    // Refresh the org list (the left org is gone), then re-scope resources by
    // switching into the personal org — setActiveOrg re-fetches networks/nodes/…
    await useOrganizations.getState().get()
    const personal = ownPersonalOrg(useOrganizations.getState().organizations, getUserId() ?? '')
    if (personal) useOrganizations.getState().setActiveOrg(personal.id)
  },
  subscribe: orgId => {
    if (!orgId) return () => {}
    const subscription = supabase
      .channel(`organization_members-${orgId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'organization_members', filter: `org_id=eq.${orgId}` },
        () => {
          void get().get(orgId)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(subscription)
    }
  },
  reset: () => set({ ...state }),
}))

export default useOrgMembers
